import { PedidoVenta, PedidoVentaDetalle } from '../services/pedidoVentaService';
import { FacturaVenta, FacturaVentaDetalle } from '../services/facturaVentaService';
import { precioListaDetallePedido } from './pedidoVentaLineas';
import ubicacionService from '../services/ubicacionService';
import transporteService from '../services/transporteService';

/**
 * Transforma un PedidoVenta a FacturaVenta para pre-cargar datos en el formulario de facturación
 * @param pedido - El pedido de venta a transformar
 * @returns FacturaVenta con los datos del pedido mapeados (incluye campos adicionales como tipoIVA y dni)
 */
export const transformarPedidoAFactura = async (pedido: PedidoVenta): Promise<FacturaVenta & { tipoIVA?: string; dni?: string }> => {
  // Transformar detalles del pedido a detalles de factura
  const detalles: FacturaVentaDetalle[] = (pedido.detalles || []).map((detalle: PedidoVentaDetalle) => {
    const detalleExt = detalle as any;
    const obtenerCampoDetalle = (variaciones: string[], defaultValue: any = undefined) => {
      for (const variacion of variaciones) {
        if (detalleExt[variacion] !== undefined && detalleExt[variacion] !== null && detalleExt[variacion] !== '') {
          return detalleExt[variacion];
        }
      }
      return defaultValue;
    };
    
    // El backend envía precioLista y porcDesc en cada detalle
    // En el estado del pedido, precioLista se guarda en precioUnitario
    // y porcDesc se guarda en descuento (como porcentaje: 20 para 20%)
    // Necesitamos tomar estos valores correctamente
    
    // Obtener precioLista: puede venir del backend o estar en precioUnitario del estado (incluye 0 explícito)
    const precioLista = precioListaDetallePedido(detalle);
    
    // Obtener porcDesc: puede venir directamente del backend o estar en descuento del estado
    let porcDesc = detalleExt.porcDesc;
    if (porcDesc === undefined || porcDesc === null) {
      // Si no hay porcDesc directo, usar descuento (que contiene el porcentaje en el estado)
      // El descuento en el estado es un porcentaje (20 para 20%), no un monto
      porcDesc = detalle.descuento || 0;
    }
    
    // Normalizar porcDesc: asegurar que esté en formato porcentaje (20 para 20%)
    // Si viene como decimal (0.20), convertir a porcentaje (20)
    if (porcDesc > 0 && porcDesc <= 1) {
      porcDesc = porcDesc * 100;
    }
    
    // Asegurar que el descuento esté en el rango válido (0-100%)
    porcDesc = Math.max(0, Math.min(100, porcDesc));
    
    const nroFormularioMasterRaw = obtenerCampoDetalle([
      'nroFormularioMaster',
      'nroformulariomaster',
      'NroFormularioMaster',
      'nro_formulario_master'
    ]);
    const nroItemMasterRaw = obtenerCampoDetalle([
      'nroItemMaster',
      'nroitemmaster',
      'NroItemMaster',
      'nro_item_master'
    ]);

    const nroFormularioMaster =
      nroFormularioMasterRaw !== undefined && nroFormularioMasterRaw !== null
        ? Number(nroFormularioMasterRaw)
        : undefined;
    const nroItemMaster =
      nroItemMasterRaw !== undefined && nroItemMasterRaw !== null
        ? Number(nroItemMasterRaw)
        : undefined;

    return {
      nroIdformulario: 0, // Se asignará al crear la factura
      nroItem: detalle.nroItem || 0,
      ...(Number.isFinite(nroFormularioMaster) && (nroFormularioMaster as number) > 0
        ? { nroFormularioMaster: nroFormularioMaster as number }
        : {}),
      ...(Number.isFinite(nroItemMaster) && (nroItemMaster as number) > 0
        ? { nroItemMaster: nroItemMaster as number }
        : {}),
      codigoArticulo: detalle.codigoArticulo,
      descripcionArticulo: detalle.descripcionArticulo,
      cantidad: detalle.cantidad,
      precioUnitario: precioLista, // Precio de lista (sin descuento)
      descuento: detalle.descuento, // Mantener el descuento original si existe
      porcDesc: porcDesc, // Porcentaje de descuento del backend (20 para 20%)
      subtotal: detalle.subtotal,
      precioUnitarioFinal: detalle.precioUnitarioFinal,
      subtotalFinal: detalle.subtotalFinal,
      notas: detalle.notas,
      um: detalle.um,
      codigoMoneda: detalle.codigoMoneda,
      cotizacion: detalle.cotizacion,
    };
  });

  // Convertir codigoMoneda de string a number si es necesario
  let codigoMoneda: number | string = pedido.codigoMoneda;
  if (typeof codigoMoneda === 'string') {
    // Si es 'ARS', convertir a 0, si es número en string, parsearlo
    if (codigoMoneda === 'ARS') {
      codigoMoneda = 0;
    } else {
      const parsed = parseInt(codigoMoneda, 10);
      codigoMoneda = isNaN(parsed) ? 0 : parsed;
    }
  }

  // Extraer campos adicionales del pedido si existen
  const pedidoExt = pedido as any;

  // Función helper para obtener un campo con diferentes variaciones de nombre
  const obtenerCampo = (variaciones: string[], defaultValue: any = '') => {
    for (const variacion of variaciones) {
      if (pedidoExt[variacion] !== undefined && pedidoExt[variacion] !== null && pedidoExt[variacion] !== '') {
        return pedidoExt[variacion];
      }
    }
    return defaultValue;
  };

  // Debug: Log para verificar qué campos de transporte están presentes en el pedido
  console.log('🔍 Campos de transporte en el pedido:', {
    nroExpreso: pedidoExt.nroExpreso,
    nroexpreso: pedidoExt.nroexpreso,
    NroExpreso: pedidoExt.NroExpreso,
    transporte: pedidoExt.transporte,
    nombreExpreso: pedidoExt.nombreExpreso,
    todosLosCampos: Object.keys(pedidoExt).filter(k => 
      k.toLowerCase().includes('expreso') || 
      k.toLowerCase().includes('transporte') ||
      k.toLowerCase().includes('exp')
    ).reduce((acc, k) => ({ ...acc, [k]: pedidoExt[k] }), {})
  });

  // Construir la factura con los datos del pedido
  // La fecha de emisión debe ser la del día actual, no la del pedido
  const fechaActual = new Date();
  const factura: FacturaVenta & { tipoIVA?: string; dni?: string } = {
    nroFormulario: 0, // Se asignará al crear la factura
    fechaEmision: fechaActual,
    horaEmision: fechaActual,
    nroTipoFormulario: pedido.nroTipoFormulario || '12',
    tipoCliente: pedido.tipoCliente || 'C',
    nroCliente: pedido.nroCliente,
    nombre: pedido.nombre,
    nroVendedor: pedido.nroVendedor,
    nroLista: pedido.nroLista,
    codigoMoneda: codigoMoneda,
    importeTotal: pedido.importeTotal || 0,
    asentado: 'N', // Nueva factura, no asentada
    descripcion: pedido.descripcion || '',
    usuario: pedido.usuario || '',
    detalles: detalles,
    porcDesc: pedido.porcDesc || 0,
    porcDesc2: pedido.porcDesc2 || 0,
    porcDesc3: pedido.porcDesc3 || 0,
    // Campos adicionales que pueden venir del pedido (con búsqueda de variaciones)
    ordenCompra: obtenerCampo(['ordenCompra', 'ordenDeCompra', 'OrdenCompra', 'OrdenDeCompra', 'ORDEN_COMPRA']),
    transporte: '', // Se asignará después de obtener el transporte
    ubicacion: '', // Se asignará después de obtener la ubicación
    formaDePago: obtenerCampo(['formaDePago', 'FormaDePago', 'FORMA_DE_PAGO', 'formaPago', 'FormaPago']),
    nroSuc: obtenerCampo(['nroSuc', 'NroSuc', 'NRO_SUC', 'nroSucursal', 'NroSucursal'], 0),
    cuit: obtenerCampo(['cuit', 'Cuit', 'CUIT']),
    idCompania: obtenerCampo(['idCompania', 'IdCompania', 'ID_COMPANIA', 'idCompañia', 'IdCompañia']),
    // Campos adicionales para UI (no están en FacturaVenta pero se usan en el formulario)
    tipoIVA: obtenerCampo(['tipoIVA', 'tipoIva', 'TipoIVA', 'TipoIva', 'TIPO_IVA']),
    dni: obtenerCampo(['dni', 'Dni', 'DNI']),
  };

  // Obtener nroExpreso directamente del pedido (el campo en el backend es "nroExpreso")
  const nroExpreso = pedidoExt.nroExpreso;

  // Debug: Log para verificar qué campos de transporte están presentes en el pedido
  console.log('🔍 Campos de transporte en el pedido:', {
    nroExpreso: pedidoExt.nroExpreso,
    tipoNroExpreso: typeof pedidoExt.nroExpreso,
    valorNroExpreso: pedidoExt.nroExpreso,
    todosLosCampos: Object.keys(pedidoExt).filter(k => 
      k.toLowerCase().includes('expreso') || 
      k.toLowerCase().includes('transporte') ||
      k.toLowerCase().includes('exp')
    ).reduce((acc, k) => ({ ...acc, [k]: pedidoExt[k] }), {})
  });

  // Obtener el nombre del transporte si hay nroExpreso
  // IMPORTANTE: El campo transporte en el formulario espera solo el número (nroExpreso) como string,
  // no el formato completo "NOMBRE (número)". El Select mostrará el nombre automáticamente.
  if (nroExpreso !== null && nroExpreso !== undefined && nroExpreso !== '' && nroExpreso !== 0) {
    console.log('✅ nroExpreso encontrado:', nroExpreso, 'Tipo:', typeof nroExpreso);
    // Guardar solo el número como string (el Select espera este formato)
    factura.transporte = String(nroExpreso);
    console.log('✅ Transporte asignado a factura (solo número):', factura.transporte);
    
    // Verificar que el transporte existe (opcional, para logging)
    try {
      const transporte = await transporteService.getTransporte(nroExpreso);
      console.log('✅ Transporte verificado:', transporte);
    } catch (error) {
      console.warn('⚠️ No se pudo verificar el transporte, pero se asignó el número:', nroExpreso);
    }
  } else {
    console.warn('⚠️ nroExpreso no válido:', nroExpreso, 'Tipo:', typeof nroExpreso);
    // Si no hay nroExpreso, buscar transporte con múltiples variaciones
    // Si viene como string con formato "NOMBRE (número)", extraer solo el número
    const transporteEncontrado = obtenerCampo([
      'transporte', 'Transporte', 'TRANSPORTE',
      'transporteOrigen', 'TransporteOrigen', 'TRANSPORTE_ORIGEN',
      'nombreTransporte', 'NombreTransporte', 'NOMBRE_TRANSPORTE',
      'nombreExpreso', 'NombreExpreso', 'NOMBRE_EXPRESO'
    ]);
    if (transporteEncontrado) {
      // Si viene en formato "NOMBRE (número)", extraer solo el número
      const match = String(transporteEncontrado).match(/\((\d+)\)/);
      if (match && match[1]) {
        factura.transporte = match[1];
        console.log('✅ Transporte extraído de formato "NOMBRE (número)":', factura.transporte);
      } else {
        // Si es solo un número, usarlo directamente
        factura.transporte = String(transporteEncontrado);
        console.log('✅ Transporte usado directamente:', factura.transporte);
      }
    } else {
      console.warn('⚠️ No se encontró transporte en ninguna variación');
    }
  }

  // Obtener el nombre de la ubicación si hay nroUbicacionOrigen
  if (pedidoExt.nroUbicacionOrigen) {
    try {
      const ubicacion = await ubicacionService.getUbicacion(pedidoExt.nroUbicacionOrigen);
      if (ubicacion) {
        factura.ubicacion = `${ubicacion.nombreUbicacion} (${ubicacion.nroUbicacion})`;
      }
    } catch (error) {
      console.warn('Error al obtener ubicación:', error);
      // Si hay error, intentar buscar en la lista completa
      try {
        const ubicaciones = await ubicacionService.getUbicaciones();
        const ubicacionEncontrada = ubicaciones.find((u: any) => u.nroUbicacion === pedidoExt.nroUbicacionOrigen);
        if (ubicacionEncontrada) {
          factura.ubicacion = `${ubicacionEncontrada.nombreUbicacion} (${ubicacionEncontrada.nroUbicacion})`;
        }
      } catch (error2) {
        console.warn('Error al obtener lista de ubicaciones:', error2);
      }
    }
  } else {
    // Si no hay nroUbicacionOrigen, buscar ubicacion con múltiples variaciones
    const ubicacionEncontrada = obtenerCampo([
      'ubicacion', 'Ubicacion', 'UBICACION', 'ubicación', 'Ubicación',
      'ubicacionOrigen', 'UbicacionOrigen', 'UBICACION_ORIGEN',
      'nombreUbicacion', 'NombreUbicacion', 'NOMBRE_UBICACION',
      'descripcionUbicacion', 'DescripcionUbicacion', 'DESCRIPCION_UBICACION'
    ]);
    if (ubicacionEncontrada) {
      factura.ubicacion = ubicacionEncontrada;
    }
  }

  return factura;
};


